import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import AppDialog from '../../components/ui/AppDialog';
import PostDataRetData from '../../services/PostDataRetData';

function defaultRange() {
  const end = dayjs();
  const start = end.subtract(7, 'day');
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
}

export default function PullShopifyOrdersDialog({ open, onClose, onSuccess }) {
  const [startDate, setStartDate] = useState(() => defaultRange().startDate);
  const [endDate, setEndDate] = useState(() => defaultRange().endDate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    const next = defaultRange();
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    setError(null);
    setResult(null);
    setSubmitting(false);
  }, [open]);

  const reset = () => {
    const next = defaultRange();
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    setError(null);
    setResult(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    if (!startDate || !endDate) {
      setError('Choose a start and end date');
      return;
    }
    if (dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      setError('End date must be on or after start date');
      return;
    }

    setSubmitting(true);
    const { data, error: postErr } = await PostDataRetData(
      { startDate, endDate },
      'api/sales/pull-shopify',
    );
    setSubmitting(false);

    if (postErr || data?.error) {
      setError(
        data?.error?.message
        || postErr?.response?.data?.error?.message
        || postErr?.message
        || 'Could not pull Shopify orders',
      );
      return;
    }

    setResult(data.data);
    onSuccess?.(data.data);
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Pull Shopify orders"
      subtitle="Fetch store orders for a date range and record matched sales into inventory."
      actions={
        <>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !!result}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Pull orders'}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={submitting || !!result}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={submitting || !!result}
          />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Already-recorded Shopify orders are skipped (idempotent by order id). Products need a Shopify variant ID mapped to match.
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <Alert severity="success">
            Fetched {result.fetched} order{result.fetched === 1 ? '' : 's'}.
            {' '}Fulfilled {result.fulfilled}.
            {result.unmatchedOnly > 0 && ` ${result.unmatchedOnly} with no matched products.`}
            {result.errors > 0 && ` ${result.errors} failed.`}
          </Alert>
        )}
      </Stack>
    </AppDialog>
  );
}
